import { useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from '../../../lib/axios';
import { useToast } from '../../components/ToastProvider';
import { useAuth } from '../../../hooks/auth';
import { PageLink, PageTitle } from '../../../_metronic/layout/core';
import { KTSVG } from '../../../_metronic/helpers';
import { Subscription } from '../companies/Companies';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import LoadingSpinner from '../../../_metronic/helpers/components/LoadingSpinner';
import Slider from 'react-slick';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import { useIntl, FormattedMessage } from 'react-intl';

type FormInputs = {
  name: string;
  address?: string;
  email?: string;
  website?: string;
  phone?: string;
  note?: string;
  status: string;
  original_admin_id?: number;
  create_subscription: boolean;
  duration: string;
  request_limit: number;
  description: string;
  start_date: Date;
  end_date: Date;
  product_list: { value: number; label: string }[];
  is_shared: boolean;
};

interface User {
  id: string;
  name: string;
  email: string;
  roles: [{ permissions: { name: string }[] }];
  company_id: string;
  subscription: Subscription;
  role: string;
  is_organization?: boolean;
  is_personal?: boolean;
  created_at: string;
  updated_at: string;
  mode: 'personal' | 'organization';
  company?: string;
  limit: limit;
  usage: usage;
  check_limit: Boolean;
}

interface limit {
  limit: number;
  is_limit_exceeded: boolean;
}

interface usage {
  usage: number;
  is_limit_exceeded: boolean;
}

type Product = {
  id: number;
  name: string;
  description: string;
  type: string;
  active?: number;
  price: number;
  quantity: number;
  created_at: string;
  updated_at: string;
  prices?: Price[];
  features?: Feature[];
};

type Price = {
  id: number;
  product_id: number;
  amount: number; // Fixed type from string to number
  currency: string;
  default_price: number;
  active: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

type Feature = {
  id: number;
  product_id: number;
  feature_name: string;
  feature_value: string;
  active: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

const AdminCreateCompany: React.FC = () => {
  const intl = useIntl();
  const { setLoading, loading } = useAuth('auth');
  const { showError, showSuccess } = useToast();
  const { register, handleSubmit, control, reset, formState: { errors }, setValue, watch, getValues } = useForm<FormInputs>({
    defaultValues: {
      start_date: undefined,
      end_date: undefined,
    },
  });
  const [users, setUsers] = useState<User[]>([]);
  const navigate = useNavigate();
  const [note, setNote] = useState<string>('');
  const [panelSubscriptionVisible, setPanelSubscriptionVisible] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [pageLoading, setPageLoading] = useState(false);

  const slider_settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get('/api/user/get-user-list');
        setUsers(response.data.data);
      } catch (error) {
        showError(intl.formatMessage({ id: 'ERROR.FAILED_FETCH_USERS', defaultMessage: 'Failed to fetch users' }));
        // Add "ERROR.FAILED_FETCH_USERS": "Failed to fetch users" to en.json
      }
    };

    const fetchProducts = async () => {
      try {
        setPageLoading(true);
        const response = await axios.get('/api/products');
        setProducts(response.data);
        setPageLoading(false);
      } catch (error) {
        setPageLoading(false);
        showError(intl.formatMessage({ id: 'ERROR.FAILED_FETCH_PRODUCTS', defaultMessage: 'Failed to fetch products' }));
        // Add "ERROR.FAILED_FETCH_PRODUCTS": "Failed to fetch products" to en.json
      }
    };
    fetchUsers();
    fetchProducts();
  }, [showError, intl]);

  const handleSelectProduct = (product: Product) => {
    const index = selectedProducts.findIndex(p => p.id === product.id);
    const updatedSelectedProducts = [...selectedProducts];
    if (index >= 0) {
      updatedSelectedProducts.splice(index, 1);
    } else {
      updatedSelectedProducts.push(product);
    }
    setSelectedProducts(updatedSelectedProducts);
    setValue('product_list', updatedSelectedProducts.map(p => ({ value: p.id, label: p.name })));
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setLoading(true);
    data.note = note;

    data.create_subscription = panelSubscriptionVisible;

    if (data.create_subscription === true) {
      // Get value duration
      data.duration = getValues('duration');
      data.request_limit = getValues('request_limit');
    }

    try {
      await axios.post('/api/companies/store', data);
      showSuccess(intl.formatMessage({ id: 'SUCCESS.COMPANY_CREATED', defaultMessage: 'Company created successfully' }));
      // Add "SUCCESS.COMPANY_CREATED": "Company created successfully" to en.json
      navigate('/companies/admin-all-companies');
    } catch (error) {
      showError(intl.formatMessage({ id: 'ERROR.FAILED_CREATE_COMPANY', defaultMessage: 'Failed to create company' }));
      // Add "ERROR.FAILED_CREATE_COMPANY": "Failed to create company" to en.json
    } finally {
      setLoading(false);
    }

  };
  const start_date = watch("start_date");
  const end_date = watch("end_date");
  const duration = watch("duration");

  useEffect(() => {
    if (start_date && duration !== 'manual') {
      const start = new Date(start_date);
      switch (duration) {
        case 'quarterly':
          start.setMonth(start.getMonth() + 3);
          break;
        case 'weekly':
          start.setDate(start.getDate() + 7);
          break;
        case 'monthly':
          start.setMonth(start.getMonth() + 1);
          break;
        case 'annually':
          start.setFullYear(start.getFullYear() + 1);
          break;
        case 'biannually':
          start.setFullYear(start.getFullYear() + 2);
          break;
        default:
          break;
      }
      setValue('end_date', start);
    } else if (start_date && duration === 'manual') {
      setValue('end_date', end_date);
    }
  }, [start_date, end_date, duration, setValue]);

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setValue('start_date', date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setValue('end_date', date);
    }
  };

  if (pageLoading) {
    return <LoadingSpinner />;
  }

  const companyBreadCrumbs: Array<PageLink> = [
    {
      title: 'COMPANY.ADMIN.ALL_COMPANIES', // Translation Key
      path: '/companies/admin-all-companies',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '', // No title for separator
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ];

  return (
    <>
      <PageTitle breadcrumbs={companyBreadCrumbs}>
        <FormattedMessage id="COMPANY.ADD_NEW_COMPANY" defaultMessage="Add New Company" />
      </PageTitle>
      <div className="app-main flex-column flex-row-fluid" id="k">
        <div className="d-flex flex-column flex-column-fluid">
          <div className="card-toolbar">
            <a href="/companies/admin-all-companies" className="btn btn-sm btn-light-primary">
              <KTSVG path="/media/icons/duotune/arrows/arr070.svg" className="svg-icon-2" />
              <FormattedMessage id="GENERAL.BACK_TO_LIST" defaultMessage="Back to List" />
            </a>
          </div>
          <div className="card mb-2">
            <div className="card-header">
              <h3 className="card-title">
                <FormattedMessage id="COMPANY.ADD_COMPANY" defaultMessage="Add Company" />
              </h3>
            </div>
            <div className="card-body">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.COMPANY_NAME" defaultMessage="Company Name" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    {...register('name', { required: intl.formatMessage({ id: 'ERROR.COMPANY_NAME_REQUIRED', defaultMessage: 'Company name is required' }) })}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.ADDRESS" defaultMessage="Address" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    {...register('address')}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.EMAIL" defaultMessage="Email" />
                  </Form.Label>
                  <Form.Control
                    type="email"
                    {...register('email', {
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: intl.formatMessage({ id: 'ERROR.INVALID_EMAIL', defaultMessage: 'Invalid email address' })
                      }
                    })}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.WEBSITE" defaultMessage="Website" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    {...register('website')}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.PHONE" defaultMessage="Phone" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    {...register('phone')}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.NOTES" defaultMessage="Notes" />
                  </Form.Label>
                  <Controller
                    name="note"
                    control={control}
                    render={({ field }) => (
                      <ReactQuill
                        value={note}
                        onChange={(e) => {
                          if (e.length <= 255) {
                            setNote(e);
                            field.onChange(e);
                          }
                        }}
                      />
                    )}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.STATUS" defaultMessage="Status" />
                  </Form.Label>
                  <Form.Control
                    as="select"
                    {...register('status', { required: intl.formatMessage({ id: 'ERROR.STATUS_REQUIRED', defaultMessage: 'Status is required' }) })}
                    isInvalid={!!errors.status}
                  >
                    <option value="">{intl.formatMessage({ id: 'FORM.SELECT_STATUS', defaultMessage: 'Select Status' })}</option>
                    <option value="active">{intl.formatMessage({ id: 'FORM.STATUS_ACTIVE', defaultMessage: 'Active' })}</option>
                    <option value="deactivated">{intl.formatMessage({ id: 'FORM.STATUS_DEACTIVATED', defaultMessage: 'Deactivated' })}</option>
                    <option value="dormant">{intl.formatMessage({ id: 'FORM.STATUS_DORMANT', defaultMessage: 'Dormant' })}</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {errors.status?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FormattedMessage id="FORM.COMPANY_OWNER" defaultMessage="Company Owner" />
                  </Form.Label>
                  <Select
                    options={users.map((user) => ({ value: user.id, label: `${user.name}, ${user.email}` }))}
                    onChange={(selectedOption) => setValue('original_admin_id', Number(selectedOption?.value))}
                    placeholder={intl.formatMessage({ id: 'FORM.SELECT_OWNER', defaultMessage: 'Select Owner' })}
                  // Add "FORM.SELECT_OWNER": "Select Owner" to en.json
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={<FormattedMessage id="FORM.SHOW_SUBSCRIPTION_PANEL" defaultMessage="Show Subscription Panel" />}
                    onChange={(e) => {
                      setPanelSubscriptionVisible(e.target.checked)
                      setValue('create_subscription', e.target.checked)
                    }}
                  />
                </Form.Group>

                {panelSubscriptionVisible && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label={<FormattedMessage id="FORM.CREATE_SUBSCRIPTION" defaultMessage="Create Subscription" />}
                        {...register('create_subscription')}
                      />
                    </Form.Group>
                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">
                        <FormattedMessage id="FORM.DESCRIPTION" defaultMessage="Description:" />
                      </label>
                      <textarea className="form-control" id="description" {...register('description')} />
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FormattedMessage id="FORM.DURATION" defaultMessage="Duration" />
                      </Form.Label>
                      <Form.Select {...register('duration', { required: intl.formatMessage({ id: 'ERROR.DURATION_REQUIRED', defaultMessage: 'Duration is required' }) })}>
                        <option value="quarterly">{intl.formatMessage({ id: 'FORM.DURATION_QUARTERLY', defaultMessage: 'Quarterly' })}</option>
                        <option value="weekly">{intl.formatMessage({ id: 'FORM.DURATION_WEEKLY', defaultMessage: 'Weekly' })}</option>
                        <option value="monthly">{intl.formatMessage({ id: 'FORM.DURATION_MONTHLY', defaultMessage: 'Monthly' })}</option>
                        <option value="annually">{intl.formatMessage({ id: 'FORM.DURATION_ANNUALLY', defaultMessage: 'Annually' })}</option>
                        <option value="biannually">{intl.formatMessage({ id: 'FORM.DURATION_BIANNUALLY', defaultMessage: 'Biannually' })}</option>
                        <option value="manual">{intl.formatMessage({ id: 'FORM.DURATION_MANUAL', defaultMessage: 'Manual' })}</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.duration?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FormattedMessage id="FORM.START_DATE" defaultMessage="Start Date" />
                      </Form.Label>
                      <Controller
                        control={control}
                        name="start_date"
                        render={({ field }) => (
                          <DatePicker
                            selected={field.value}
                            onChange={(date) => handleStartDateChange(date)}
                            className="form-control"
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.start_date?.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FormattedMessage id="FORM.END_DATE" defaultMessage="End Date" />
                      </Form.Label>
                      <Controller
                        control={control}
                        name="end_date"
                        render={({ field }) => (
                          <DatePicker
                            selected={field.value}
                            onChange={(date) => handleEndDateChange(date)}
                            className="form-control"
                          />
                        )}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <div className="mb-3">
                        <label htmlFor="request_limit" className="form-label">
                          <FormattedMessage id="FORM.REQUEST_LIMIT" defaultMessage="Request Limit:" />
                        </label>
                        <input
                          className={`form-control ${errors.request_limit ? 'is-invalid' : ''}`}
                          type="number"
                          id="request_limit"
                          {...register('request_limit', {
                            valueAsNumber: true,
                            min: 0,
                            required: panelSubscriptionVisible ? intl.formatMessage({ id: 'ERROR.REQUEST_LIMIT_REQUIRED', defaultMessage: 'Request limit is required' }) : false
                          })}
                        />
                        {errors.request_limit && <div className="invalid-feedback">{errors.request_limit.message}</div>}
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label={<FormattedMessage id="FORM.IS_SHARED" defaultMessage="Is Shared" />}
                        {...register('is_shared')}
                      />
                    </Form.Group>
                    <Slider {...slider_settings}>
                      {products.map(product => (
                        <div key={product.id} className="card card-custom gutter-b">
                          <div className="card-header">
                            <div className="card-title">
                              <h3 className="card-label">{product.name}</h3>
                            </div>
                            <div className="card-toolbar">
                              <button
                                type="button"
                                className="btn btn-sm btn-info font-weight-bold"
                                onClick={() => handleSelectProduct(product)}
                              >
                                <i className="fa-solid fa-bolt"></i>
                                <FormattedMessage id="FORM.SELECT_PRODUCT" defaultMessage="Select" />
                              </button>
                            </div>
                          </div>
                          <div className="card-body">
                            <FormattedMessage
                              id="FORM.PRICE"
                              defaultMessage="Price:"
                            /> {product.price} {product.prices?.[0]?.currency || 'MYR'}
                            <table>
                              <thead>
                                <tr>
                                  <th>
                                    <FormattedMessage id="FORM.FEATURE_NAME" defaultMessage="Name" />
                                  </th>
                                  <th>
                                    <FormattedMessage id="FORM.FEATURE_VALUE" defaultMessage="Features Included" />
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {product.features?.map((feature: any) => (
                                  <tr key={feature.id}>
                                    <td>{feature.feature_name}</td>
                                    <td>
                                      {feature.feature_value === 'Yes' ? (
                                        <span style={{ color: 'green' }}>
                                          <i className="fa-solid fa-circle-check"></i>
                                          <FormattedMessage id="FORM.YES" defaultMessage="Yes" />
                                        </span>
                                      ) : (
                                        <span style={{ color: 'red' }}>
                                          <i className="fa-solid fa-circle-xmark"></i>
                                          <FormattedMessage id="FORM.NO" defaultMessage="No" />
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </Slider>
                    <div className='mb-3'>
                      <label htmlFor="product_list" className="form-label">
                        <FormattedMessage id="FORM.PRODUCT_LIST" defaultMessage="Product List:" />
                      </label>
                      <Controller
                        name="product_list"
                        control={control}
                        rules={{ required: intl.formatMessage({ id: 'ERROR.SELECT_PRODUCT', defaultMessage: 'Please select a product.' }) }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={products.map(product => ({ value: product.id, label: product.name }))}
                            isMulti
                            isSearchable
                            placeholder={intl.formatMessage({ id: 'FORM.SELECT_PRODUCTS', defaultMessage: 'Select Products' })}
                          />
                        )}
                      />
                      {errors.product_list && <div className="invalid-feedback">{errors.product_list.message}</div>}
                    </div>
                  </>
                )}

                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : <FormattedMessage id="FORM.SUBMIT" defaultMessage="Submit" />}
                </Button>
                <Button variant="secondary" type="button" className="btn btn-secondary" onClick={() => reset()}>
                  <FormattedMessage id="FORM.RESET" defaultMessage="Reset" />
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminCreateCompany;
